import { buildAnswersFromRows } from '../audit/answerRows';
export { buildAnswersFromRows, type ResponseRow } from '../audit/answerRows';
import { QUALITY_MAX_AGE_DAYS } from '../audit/grades';
import { partyOf, type Party } from '../audit/requirements';
import { GraphError } from '../graph/client';
import { apiFetchBlob } from '../api/client';
import { dataViaApi } from '../api/mode';
import { parseFailureSummary, summarizeAudit, type FailureSummary } from '../audit/failureSummary';
import { hasKpis, parseKpiSummary, summarizeKpis, type KpiSummary } from '../audit/kpis';
import type { TemplateDefinition } from '../audit/template';
import { graphFetchBlob } from '../graph/client';
import { getListItem, getListItems, odataString, updateListItem } from '../graph/lists';
import { getSiteId } from '../graph/site';
import { LISTS, READ_CACHE_MS } from './listNames';
import type { ResponseRow } from '../audit/answerRows';
import type { ServerAuditState } from '../local/types';

/** An Audits header row as read from SharePoint. */
export interface ServerAudit {
  id: number;
  storeId: number;
  templateId: number;
  templateVersion: number;
  auditorUpn: string;
  status: string;
  startedAt: string;
  /** localId of the device session that created it (DeviceSyncState JSON). */
  originLocalId: string | null;
}

interface AuditFields {
  StoreId: number;
  TemplateId: number;
  TemplateVersion: number;
  AuditorUpn: string;
  AuditStatus: string;
  StartedAt: string;
  SubmittedAt: string;
  Score: number | null;
  SectionScores: string;
  CriticalFailed: boolean;
  DeviceSyncState: string;
  FailureSummary: string;
  /** Visit KPIs (2026-10-20): number/percent answers, written at submit (src/audit/kpis.ts). */
  KpiSummary: string;
  /** 'store' = a store team's own audit (owner rulings 2026-10-19); blank = staff. */
  Party: string;
  /** 'audit-<localId>' — names the create so a resend never makes a second row (src/data/clientKey.ts). */
  ClientKey?: string;
}

function ownerFromSyncState(raw: string | undefined): string | null {
  try {
    const state = JSON.parse(raw ?? '{}') as { localId?: string };
    return state.localId ?? null;
  } catch {
    return null; // Malformed sync state — treat as unknown origin.
  }
}

/** Status + owning device of one Audits row; null if the row is gone. */
export async function getAuditState(auditId: number): Promise<ServerAuditState | null> {
  const item = await getListItem<AuditFields>(LISTS.audits, auditId);
  if (!item) return null;
  return {
    status: item.fields.AuditStatus ?? '',
    ownerLocalId: ownerFromSyncState(item.fields.DeviceSyncState),
  };
}

/**
 * Close an audit server-side with no local copy involved (cancelling one
 * that was started on another device, or expiring an abandoned row).
 * The owning device notices on its next sync and stops.
 */
export async function setServerAuditStatus(
  auditId: number,
  status: 'cancelled' | 'expired',
): Promise<void> {
  await updateListItem(LISTS.audits, String(auditId), { AuditStatus: status });
}

/**
 * Cross-device resume: make THIS device's session the owner of the audit,
 * so the previous device stops pushing answers over the newer ones.
 */
export async function claimServerAudit(auditId: number, localId: string): Promise<void> {
  await updateListItem(LISTS.audits, String(auditId), {
    DeviceSyncState: JSON.stringify({ localId }),
  });
}

/** My in-progress audits on the server (AuditorUpn + AuditStatus indexed). */
export async function getMyOpenServerAudits(upn: string): Promise<ServerAudit[]> {
  const items = await getListItems<AuditFields>(LISTS.audits, {
    filter: `fields/AuditorUpn eq ${odataString(upn)} and fields/AuditStatus eq ${odataString('in_progress')}`,
  });
  return items.map((i) => {
    const originLocalId = ownerFromSyncState(i.fields.DeviceSyncState);
    return {
      id: Number(i.id),
      storeId: i.fields.StoreId ?? 0,
      templateId: i.fields.TemplateId ?? 0,
      templateVersion: i.fields.TemplateVersion ?? 0,
      auditorUpn: i.fields.AuditorUpn ?? '',
      status: i.fields.AuditStatus ?? '',
      startedAt: i.fields.StartedAt ?? '',
      originLocalId,
    };
  });
}

/** A submitted (or any) Audits row with its stored results. */
export interface AuditRecord {
  id: number;
  storeId: number;
  templateId: number;
  templateVersion: number;
  auditorUpn: string;
  status: string;
  startedAt: string;
  submittedAt: string;
  /** Stored at 2 decimals by the sync transport; null = all-N/A audit. */
  score: number | null;
  criticalFailed: boolean;
  sectionScores: Record<string, number | null> | null;
  /** Phase 6b pass/fail summary; null on audits submitted before 6b (or unreadable). */
  failureSummary: FailureSummary | null;
  /** Number/percent answers (Visit KPIs); null on audits submitted before 2026-10-20 (or unreadable). */
  kpis: KpiSummary | null;
  /** Who conducted it as: 'store' = the store team's own audit (its score never feeds the grade). */
  party: Party;
}

export function toAuditRecord(id: string, f: Partial<AuditFields>): AuditRecord {
  let sectionScores: Record<string, number | null> | null = null;
  if (f.SectionScores) {
    try {
      sectionScores = JSON.parse(f.SectionScores) as Record<
        string,
        number | null
      >;
    } catch {
      // Malformed JSON — the per-section breakdown is cosmetic, drop it.
    }
  }
  return {
    id: Number(id),
    storeId: f.StoreId ?? 0,
    templateId: f.TemplateId ?? 0,
    templateVersion: f.TemplateVersion ?? 0,
    auditorUpn: f.AuditorUpn ?? '',
    status: f.AuditStatus ?? '',
    startedAt: f.StartedAt ?? '',
    submittedAt: f.SubmittedAt ?? '',
    // Member-writable (risk #1): a value no audit can produce reads as "no
    // score" rather than skewing grades, averages and the 0–100 chart axis.
    score: typeof f.Score === 'number' && Number.isFinite(f.Score) && f.Score >= 0 && f.Score <= 100 ? f.Score : null,
    criticalFailed: f.CriticalFailed ?? false,
    sectionScores,
    failureSummary: parseFailureSummary(f.FailureSummary),
    kpis: parseKpiSummary(f.KpiSummary),
    party: partyOf(f.Party),
  };
}

/**
 * Submitted audits, newest first (AuditStatus is indexed; SubmittedAt is
 * sorted client-side to keep the query on one indexed column). Role scoping
 * is applied by the caller against its visible stores.
 */
export async function getSubmittedAudits(
  options: {
    /** Only audits submitted on/after this ISO time (SubmittedAt is indexed). */
    since?: string;
    /** Leave out the big JSON columns (section scores, failure summary). */
    slim?: boolean;
    /** Slim columns + the KPI summary only (Analytics → KPIs). */
    kpis?: boolean;
  } = {},
): Promise<AuditRecord[]> {
  // G5: bounded by date + slim columns, so pages don't download every audit
  // ever submitted (≈10 MB by year 3, past SharePoint's 5,000 limit with
  // visits). The date clause comes FIRST (SharePoint narrows by the first
  // indexed clause; "submitted" alone grows without limit).
  const status = `fields/AuditStatus eq ${odataString('submitted')}`;
  const select = options.kpis
    ? { select: [...SLIM_AUDIT_COLUMNS, 'KpiSummary'] }
    : options.slim
      ? { select: SLIM_AUDIT_COLUMNS }
      : {};
  let items;
  try {
    items = await getListItems<AuditFields>(LISTS.audits, {
      filter: options.since ? `fields/SubmittedAt ge ${odataString(options.since)} and ${status}` : status,
      ...select,
      // Home, district pages, the Audits hub and Analytics all read this (round 2,
      // 2026-10-22: 4–7 s each). A write to Audits from this tab (sync included)
      // drops it at once; other devices' audits show within a minute.
      cacheMs: READ_CACHE_MS,
    });
  } catch (err) {
    // First date/select filter of its kind in this tenant: if SharePoint
    // refuses it, read the old way (unbounded) rather than break the page.
    if (!(options.since || options.slim || options.kpis) || !(err instanceof GraphError && err.status === 400)) throw err;
    const all = await getListItems<AuditFields>(LISTS.audits, { filter: status });
    items = options.since ? all.filter((i) => (i.fields.SubmittedAt ?? '') >= options.since!) : all;
  }
  return items
    .map((i) => toAuditRecord(i.id, i.fields))
    .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
}

/** Audit columns list pages need (no SectionScores / FailureSummary JSON). */
const SLIM_AUDIT_COLUMNS = [
  'Title', 'StoreId', 'TemplateId', 'TemplateVersion', 'AuditorUpn', 'AuditStatus', 'StartedAt', 'SubmittedAt',
  'Score', 'CriticalFailed', 'Party',
] as const;

/** How far back the dashboards and lists look (older audits stay on each store's page). */
/** The same window Quality is limited to (audit/grades.ts). */
export const RECENT_AUDIT_DAYS = QUALITY_MAX_AGE_DAYS;

/**
 * ISO time RECENT_AUDIT_DAYS ago, rounded down to the hour — the same text
 * for every page in that hour, so the cached audits read is shared.
 */
export function recentAuditsSince(now = new Date()): string {
  const t = now.getTime() - RECENT_AUDIT_DAYS * 86_400_000;
  return new Date(t - (t % 3_600_000)).toISOString();
}

/** Submitted audits for one store, newest first (StoreId + AuditStatus indexed). */
export async function getSubmittedAuditsForStore(
  storeId: number,
  /** Only the slim columns + KpiSummary (the form's KPI history, B8) — not every answer summary. */
  opts: { kpisOnly?: boolean } = {},
): Promise<AuditRecord[]> {
  const items = await getListItems<AuditFields>(LISTS.audits, {
    filter: `fields/StoreId eq ${storeId} and fields/AuditStatus eq ${odataString('submitted')}`,
    ...(opts.kpisOnly ? { select: [...SLIM_AUDIT_COLUMNS, 'KpiSummary'] } : {}),
  });
  return items
    .map((i) => toAuditRecord(i.id, i.fields))
    .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
}

/** One audit header by item id; null if it doesn't exist. */
export async function getAuditRecord(
  auditId: number,
): Promise<AuditRecord | null> {
  const item = await getListItem<AuditFields>(LISTS.audits, auditId);
  return item ? toAuditRecord(item.id, item.fields) : null;
}

/** A saved audit note as read back from AuditNotes. */
export interface AuditNoteRow {
  id: number;
  /** null = general (audit-level) note. */
  questionId: string | null;
  /** Set when the note targets one sample-group entry. */
  sampleIndex: number | null;
  text: string;
  author: string;
  /** ISO timestamp from SharePoint. */
  created: string;
}

interface NoteFields {
  QuestionId: string;
  SampleIndex: number | null;
  NoteText: string;
  /** Since 2026-10-20 (before that no note could be saved — see data-model.md). */
  NoteAuthorUpn: string;
  /** Server-stamped since 2026-10-20; older rows fall back to Created. */
  PostedAt?: string;
  /** 'note-<local note id>' (src/data/clientKey.ts). */
  ClientKey?: string;
}

/** All notes for one audit (AuditId indexed). */
export async function getNotesForAudit(
  auditId: number,
): Promise<AuditNoteRow[]> {
  const items = await getListItems<NoteFields>(LISTS.auditNotes, {
    filter: `fields/AuditId eq ${auditId}`,
  });
  return items.map((i) => ({
    id: Number(i.id),
    questionId: i.fields.QuestionId ? i.fields.QuestionId : null,
    sampleIndex: i.fields.SampleIndex ?? null,
    text: i.fields.NoteText ?? '',
    author: i.fields.NoteAuthorUpn ?? '',
    created: i.fields.PostedAt || i.createdDateTime,
  }));
}

/** An uploaded file from the AuditAttachments library. */
export interface AuditAttachmentRow {
  id: number;
  questionId: string;
  sampleIndex: number | null;
  kind: string;
  filename: string;
  url: string;
  uploadedBy: string;
}

interface AttachmentFields {
  FileLeafRef: string;
  QuestionId: string;
  SampleIndex: number | null;
  AttachmentType: string;
  UploadedBy: string;
}

/** All uploaded attachments for one audit (AuditId indexed). */
export async function getAttachmentsForAudit(
  auditId: number,
): Promise<AuditAttachmentRow[]> {
  const items = await getListItems<AttachmentFields>(LISTS.attachmentsLibrary, {
    filter: `fields/AuditId eq ${auditId}`,
  });
  return items
    .filter((i) => i.webUrl) // skip the folder row itself if it matches
    .map((i) => ({
      id: Number(i.id),
      questionId: i.fields.QuestionId ?? '',
      sampleIndex: i.fields.SampleIndex ?? null,
      kind: i.fields.AttachmentType ?? 'photo',
      filename: i.fields.FileLeafRef ?? 'attachment',
      url: i.webUrl!,
      uploadedBy: i.fields.UploadedBy ?? '',
    }));
}

/** Download one attachment's bytes (token-authenticated, works cross-user). */
export async function downloadAuditAttachment(itemId: number): Promise<Blob> {
  // Server mode: the server checks the viewer may see the file's audit/task.
  if (dataViaApi()) return apiFetchBlob(`/files/${itemId}`);
  const siteId = await getSiteId();
  return graphFetchBlob(
    `/sites/${siteId}/lists/${LISTS.attachmentsLibrary}/items/${itemId}/driveItem/content`,
  );
}

/**
 * The submitted audit as a PDF, made by the server (GET /api/audits/{id}/pdf
 * — the caller's read policy applies). Server mode only: the direct-to-
 * SharePoint rollback mode has no PDF.
 */
export async function downloadAuditPdf(auditId: number): Promise<Blob> {
  if (!dataViaApi()) throw new Error('PDF download needs the AuditPlus server (it’s off in this mode).');
  return apiFetchBlob(`/audits/${auditId}/pdf`);
}

interface ResponseFields {
  QuestionId: string;
  SampleIndex: number | null;
  ValueJson: string;
}

/** All synced responses for one audit (AuditId indexed). */
export async function getResponsesForAudit(
  auditId: number,
): Promise<ResponseRow[]> {
  const items = await getListItems<ResponseFields>(LISTS.auditResponses, {
    filter: `fields/AuditId eq ${auditId}`,
  });
  return items.map((i) => ({
    itemId: Number(i.id),
    questionId: i.fields.QuestionId ?? '',
    sampleIndex: i.fields.SampleIndex ?? null,
    valueJson: i.fields.ValueJson ?? 'null',
  }));
}


/**
 * One-time backfill (Phase 6b, Analytics (admins only) → "Build
 * missing summaries"): audits submitted before 6b have no FailureSummary, and those
 * before 2026-10-20 no KpiSummary (only matters on templates with number/
 * percent questions — needsSummary). Rebuild
 * each one's answers from its synced AuditResponses, summarize them against
 * the template version the audit was PINNED to, and write only that column.
 * Sequential and resumable: each audit that's done stays done; a failure
 * stops the run and says which audit.
 */
/**
 * A submitted audit the backfill should (re)summarize: no failure summary, or
 * no KPI summary on a template version that has KPIs. An unreadable version
 * (null) counts when the failure summary is missing (reported as skipped).
 */
export function needsSummary(a: AuditRecord, def: TemplateDefinition | null): boolean {
  if (a.status !== 'submitted') return false;
  return a.failureSummary === null || (a.kpis === null && hasKpis(def));
}

export async function backfillFailureSummaries(
  audits: readonly AuditRecord[],
  definitionFor: (templateId: number) => TemplateDefinition | null,
  onProgress: (done: number, total: number) => void,
): Promise<{ written: number; skipped: number }> {
  const todo = audits.filter((a) => needsSummary(a, definitionFor(a.templateId)));
  let written = 0;
  let skipped = 0;
  for (const a of todo) {
    const def = definitionFor(a.templateId);
    if (!def) {
      skipped++; // its template version can't be read — nothing to summarize against
    } else {
      const { answers } = buildAnswersFromRows(await getResponsesForAudit(a.id));
      // Only the missing column(s): a summary already there is never rewritten.
      await updateListItem<Partial<Pick<AuditFields, 'FailureSummary' | 'KpiSummary'>>>(LISTS.audits, String(a.id), {
        ...(a.failureSummary === null ? { FailureSummary: JSON.stringify(summarizeAudit(def, answers)) } : {}),
        ...(a.kpis === null && hasKpis(def) ? { KpiSummary: JSON.stringify(summarizeKpis(def, answers)) } : {}),
      });
      written++;
    }
    onProgress(written + skipped, todo.length);
  }
  return { written, skipped };
}
